/* Optional effects enhance the server-rendered final value, once per instance. */
if (!customElements.get('scarcity-bar')) {
  const scarcityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          scarcityObserver.unobserve(entry.target);
          entry.target.animateOnce();
        }
      }, { threshold: 0.3 })
    : null;

  class ScarcityBar extends HTMLElement {
    #played = false;
    #frame = null;
    #motion = null;
    #onMotionChange = () => {
      if (this.#motion.matches) this.cancelAnimations();
    };

    connectedCallback() {
      if (this.#played) return;
      this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.#motion.matches || window.Shopify?.designMode || !scarcityObserver) {
        this.cancelAnimations();
        return;
      }

      const inline = this.dataset.sbStyle === 'inline-card';
      const fill = this.dataset.sbAnimateFill === 'true';
      const count = inline && this.dataset.sbAnimateCount === 'true';
      const glow = inline && this.dataset.sbGlow === 'true';
      if (!fill && !count && !glow) return;

      this.#motion.addEventListener('change', this.#onMotionChange);
      if (fill) this.classList.add('scarcity-bar--armed');
      scarcityObserver.observe(this);
    }

    animateOnce() {
      if (this.#played || !this.isConnected) return;
      this.#played = true;
      if (this.dataset.sbStyle !== 'inline-card') {
        this.classList.add('is-visible');
        return;
      }

      const target = Number(this.dataset.sbPercent);
      if (this.dataset.sbGlow === 'true' && target > 0) {
        this.classList.add('scarcity-bar--glowing');
      }
      const fill = this.dataset.sbAnimateFill === 'true' ? this.querySelector('.scarcity-bar__fill') : null;
      const tokens = this.dataset.sbAnimateCount === 'true' ? this.querySelectorAll('.scarcity-bar__percent') : [];
      if ((!fill && !tokens.length) || target <= 0) {
        this.classList.remove('scarcity-bar--armed');
        return;
      }
      const label = this.querySelector('.scarcity-bar__right');
      if (tokens.length && !label?.querySelector('a[href]')) label?.setAttribute('aria-hidden', 'true');
      const duration = Math.min(5, Math.max(1, Number(this.dataset.sbCountDuration) || 4)) * 1000;
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - (1 - progress) ** 3;
        const value = Math.round(target * eased);
        if (fill) fill.style.transform = `scaleX(${eased})`;
        for (const token of tokens) token.textContent = `${value}%`;
        if (progress < 1) {
          this.#frame = requestAnimationFrame(tick);
        } else {
          this.#frame = null;
          fill?.style.removeProperty('transform');
          this.classList.remove('scarcity-bar--armed');
          label?.removeAttribute('aria-hidden');
        }
      };
      for (const token of tokens) token.textContent = '0%';
      this.#frame = requestAnimationFrame(tick);
    }

    /** Stop pending effects before a variant update or removal. Never replay. */
    cancelAnimations() {
      this.#played = true;
      scarcityObserver?.unobserve(this);
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
      this.querySelector('.scarcity-bar__fill')?.style.removeProperty('transform');
      this.classList.remove('scarcity-bar--armed', 'is-visible', 'scarcity-bar--glowing');
      for (const token of this.querySelectorAll('.scarcity-bar__percent')) {
        token.textContent = `${this.dataset.sbPercent}%`;
      }
      this.querySelector('.scarcity-bar__right')?.removeAttribute('aria-hidden');
    }

    disconnectedCallback() {
      this.cancelAnimations();
      this.#motion?.removeEventListener('change', this.#onMotionChange);
    }
  }

  customElements.define('scarcity-bar', ScarcityBar);
}
